import {useEffect, useRef, useState} from "react";
import {Alert, Badge, Button, Card, ListGroup, Spinner} from "react-bootstrap";
import {Link, useLocation, useParams, useSearchParams} from "react-router";
import {ProductsApi} from "../../api/ProductsApi";
import {ProfileApi} from "../../api/ProfileApi";
import {EarlyAccessUserClaim, OperatingSystem, ProductFileViewModel, ProductVersionViewModel} from "../../api/models";
import {usePublishedProduct} from "./usePublishedProduct";
import {useDownloadAccess} from "./useDownloadAccess";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {CopyButton} from "../../components/CopyButton";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {detectOperatingSystem, OperatingSystemLabel} from "../../components/OperatingSystemLabel";
import {formatBytes, formatCalendarDate} from "../../components/format";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";
import {format, products, rich} from "../../i18n";

// For anyone: a released product runs as a demo until activated, so its installers are free to download (a product in
// Early Access's are for its license holders, signed in, and nobody else sees them listed). The update check sends the
// products here: /products/{sku}/download?version=1.2.0.
// Early Access Versions follow, for Early Access Users with an Early Access license (and administrators); the API
// refuses anyone else, which just leaves them out. Someone whose only licenses for the product are Early Access ones sees
// just the Early Access Versions: their license can't activate a released version, which would only run as a demo.
// Each file's downloadUrl adds &file={fileId}, which starts that file's download once the page has loaded.
export function ProductDownloadPage() {
    const {sku = ''} = useParams();
    const [search] = useSearchParams();
    const wanted = search.get('version') ?? '';
    const wantedFile = search.get('file') ?? '';
    const {data: product, error, loading} = usePublishedProduct(sku);
    const [downloading, setDownloading] = useState<string>();
    const [downloadError, setDownloadError] = useState<unknown>();
    const autoStarted = useRef(false);
    const [yourOs] = useState(detectOperatingSystem);
    const location = useLocation();
    const {access, error: accessError} = useDownloadAccess(product);
    const {user, isAdmin} = useAuth();
    const mayHaveEarlyAccess = !!product && !!user && (isAdmin || !!user.claims?.includes(EarlyAccessUserClaim));
    const earlyAccess = useAsync(
        async () => mayHaveEarlyAccess ? await ProductsApi.earlyAccessVersions(product!.id).catch(() => []) : [],
        [mayHaveEarlyAccess, product?.id, user?.username]);
    const earlyAccessVersions = earlyAccess.data ?? [];
    const licenses = useAsync(
        async () => mayHaveEarlyAccess && !isAdmin ? await ProfileApi.licenses().catch(() => []) : [],
        [mayHaveEarlyAccess, isAdmin, user?.username]);
    const ownLicenses = (licenses.data ?? []).filter(x => x.productId === product?.id && !x.revoked);
    const earlyAccessOnly = earlyAccessVersions.length > 0 && ownLicenses.length > 0 && ownLicenses.every(x => x.kind === 'earlyAccess');

    // The version asked for (the update check names it), or else the current one: in each list, the current one of its kind.
    const wantedVersion = product?.versions.find(x => x.version === wanted) ?? earlyAccessVersions.find(x => x.version === wanted);
    const highlighted = wantedVersion ?? product?.currentVersion;
    const highlightedEarlyAccess = wantedVersion ? (wantedVersion.earlyAccess ? wantedVersion : undefined) : earlyAccessVersions.find(x => x.isCurrent);

    const download = async (version: ProductVersionViewModel, file: ProductFileViewModel) => {
        if (!product) {
            return;
        }
        setDownloading(file.id);
        setDownloadError(undefined);
        try {
            // The URL lasts a few minutes, so fetch it at the click. S3 sends it as an attachment, so the page stays.
            const target = await ProductsApi.downloadUrl(product.id, version.id, file.id);
            window.location.assign(target.url);
        } catch (err) {
            setDownloadError(err);
        } finally {
            setDownloading(undefined);
        }
    };

    useEffect(() => {
        if (highlighted) {
            document.getElementById(`version-${highlighted.id}`)?.scrollIntoView({ block: 'center' });
        }
    }, [highlighted]);

    useEffect(() => {
        const file = highlighted?.files.find(x => x.id === wantedFile);
        if (access === 'allowed' && highlighted && file && !autoStarted.current) {
            autoStarted.current = true;
            void download(highlighted, file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access, highlighted, wantedFile]);

    return (
        <>
            <div className="mb-2"><Link to={`/products/${encodeURIComponent(sku)}`} className="small"><i className="bi bi-arrow-left me-1"></i>{product?.name ?? products.downloads.product}</Link></div>
            <ErrorAlert error={error} messages={{ 404: products.noSuchProduct }}/>
            {loading && !product && <LoadingSpinner/>}
            {product && (
                <>
                    <PageHeader title={format(products.downloads.title, { product: product.name })}/>
                    <ErrorAlert error={downloadError} onClose={() => setDownloadError(undefined)} messages={{
                        no_license: format(products.downloads.noLicense, { product: product.name }),
                        401: format(products.downloads.signInForBeta, { product: product.name }),
                        404: products.downloads.fileGone
                    }}/>
                    <ErrorAlert error={accessError}/>
                    {access === 'checking' && !accessError && <LoadingSpinner/>}
                    {access === 'signed-out' && (
                        <Alert variant="secondary">
                            {rich(products.downloads.signInToSeeBeta,
                                { link: text => <Link to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}>{text}</Link> },
                                { product: product.name })}
                        </Alert>
                    )}
                    {access === 'no-license' && <Alert variant="secondary">{format(products.downloads.noLicenseForBeta, { product: product.name })}</Alert>}
                    {access === 'allowed' && !earlyAccessOnly && (
                        <>
                        {wanted && !earlyAccess.loading && ![...product.versions, ...earlyAccessVersions].some(x => x.version === wanted) && (
                            <Alert variant="warning">{format(products.downloads.versionMissing, { version: wanted })}</Alert>
                        )}
                        {wantedFile && highlighted && !highlighted.files.some(x => x.id === wantedFile) && (
                            <Alert variant="warning">{products.downloads.fileMissing}</Alert>
                        )}
                        {product.versions.length === 0 && <Alert variant="secondary">{products.downloads.nothingYet}</Alert>}
                        {product.versions.map(version => (
                            <VersionCard key={version.id} version={version} highlighted={version.id === highlighted?.id} yourOs={yourOs}
                                         downloading={downloading} onDownload={file => download(version, file)}/>
                        ))}
                        </>
                    )}
                    {earlyAccessVersions.length > 0 && (
                        <section aria-labelledby="early-access-versions" className="mt-4">
                            <h2 id="early-access-versions" className="h4">{products.downloads.earlyAccessTitle}</h2>
                            <p className="text-body-secondary">{products.downloads.earlyAccessIntro}</p>
                            {earlyAccessVersions.map(version => (
                                <VersionCard key={version.id} version={version} highlighted={version.id === highlightedEarlyAccess?.id} yourOs={yourOs}
                                             downloading={downloading} onDownload={file => download(version, file)}/>
                            ))}
                        </section>
                    )}
                    <TrademarkNotices marks={trademarksIn(product.description)} className="mt-4"/>
                </>
            )}
        </>
    );
}

// A version and its files, each with a download button. highlighted: the one asked for (or the current one), whose file
// for the visitor's operating system is the suggested download.
function VersionCard({version, highlighted, yourOs, downloading, onDownload}: {
    version: ProductVersionViewModel,
    highlighted: boolean,
    yourOs: OperatingSystem | undefined,
    downloading: string | undefined,
    onDownload(file: ProductFileViewModel): void
}) {
    return (
        <Card id={`version-${version.id}`} className="mb-3" border={highlighted ? 'primary' : undefined}>
            <Card.Body>
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <h2 className="h5 mb-0">{format(products.downloads.version, { version: version.version })}</h2>
                    {version.isCurrent && <Badge bg="success">{products.downloads.current}</Badge>}
                    {version.earlyAccess && <Badge bg="info" text="dark">{products.downloads.earlyAccessBadge}</Badge>}
                    <span className="text-body-secondary small">{format(products.downloads.released, { date: formatCalendarDate(version.releasedOn) })}</span>
                </div>
                {version.releaseNotes && <p className="product-text mt-3 mb-0">{version.releaseNotes}</p>}
            </Card.Body>
            <ListGroup variant="flush">
                {version.files.map(file => {
                    const suggested = highlighted && file.operatingSystem === yourOs;
                    return (
                        <ListGroup.Item key={file.id} className="d-flex flex-wrap align-items-center gap-2">
                            <span className="product-file-os"><OperatingSystemLabel os={file.operatingSystem}/></span>
                            <span className="me-auto">
                                {file.title}
                                <span className="d-block small text-body-secondary text-break">
                                    {file.fileName}{file.fileSize !== undefined && ` · ${formatBytes(file.fileSize)}`}
                                </span>
                                {file.sha256 && (
                                    <span className="d-flex align-items-center gap-2 small text-body-secondary mt-1">
                                        <span className="text-break">{products.downloads.sha256} <span className="font-monospace">{file.sha256}</span></span>
                                        <CopyButton text={file.sha256} label={format(products.downloads.copySha256, { file: file.fileName })}/>
                                    </span>
                                )}
                            </span>
                            <Button size="sm" variant={suggested ? 'primary' : 'outline-primary'} disabled={!!downloading}
                                    onClick={() => onDownload(file)}>
                                {downloading === file.id ? <Spinner size="sm" className="me-2"/> : <i className="bi bi-download me-2"></i>}
                                {products.download}
                            </Button>
                        </ListGroup.Item>
                    );
                })}
            </ListGroup>
        </Card>
    );
}
